// packages/api/src/PublicRecurringGiftService.ts
//
// ROADMAP #204 step 6a — MONTHLY GIVING: the donor's manage link (`/giving/<token>` on the site, in every monthly
// thank-you). Anonymous — the signed token IS the proof; a new token revokes every link sent before.
//   get         `publicRecurringGift`: the gift (the cause, the organizer, the amount each month, its status, the next
//               charge). A READ — nothing changes on load (mail scanners open every link).
//   cancel      `publicRecurringGiftCancel`: stop it now (at Stripe); answers the gift as it is after.
//   cardStart   `publicRecurringGiftCardStart`: a SetupIntent for a new card (Stripe.js `confirmSetup` with its secret, on
//               the host's connected account — null in test mode).
//   cardFinish  `publicRecurringGiftCardFinish`: the confirmed SetupIntent becomes the gift's card for every next month.
// eventsmagic-api: src/app/graphql/types/recurringGiftTypes.ts, src/core/RecurringGiftCore.ts.
//
// ⚠ A bad or old link is refused NOT_FOUND on `token` ("This link is not valid any more…"); a stopped gift's card start
//   on `token` too; a card the server could not confirm on `setupIntentId`.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

/** The gift's statuses (mirror of the API's RECURRING_GIFT_STATUS). */
export const RECURRING_GIFT_STATUS = {
  /** The first card payment is not confirmed yet. */
  INCOMPLETE: "incomplete",
  ACTIVE: "active",
  /** A charge failed — Stripe retries; a new card fixes it. */
  PAST_DUE: "past_due",
  CANCELED: "canceled",
  /** #296 — the organizer revoked us at Stripe: it may still charge, and only they can stop it. */
  DISCONNECTED: "disconnected",
} as const;

/** The donor's view of their monthly gift — no ids. */
export interface PublicRecurringGift {
  /** #204 step 6b — donation (a monthly gift) | membership (an auto-renewing membership). */
  lineKind?: string | null;
  causeName?: string | null;
  /** An auto-renewing membership's type name. */
  membershipName?: string | null;
  organizerName?: string | null;
  /** The organizer page (`/o/<slug>`) — null when the gift has none to link to. */
  organizerSlug?: string | null;
  currencyCode?: string | null;
  /** The gift each month. */
  amount?: number | null;
  /** What is charged each month (the gift + fees when the donor covers them). */
  chargeAmount?: number | null;
  /** 'month' */
  billingInterval?: string | null;
  /** incomplete | active | past_due | canceled */
  recurringStatus?: string | null;
  isTest?: boolean | null;
  /** The donor gets a yearly summary receipt (in January). */
  wantsReceipt?: boolean | null;
  startedAt?: string | null;
  nextChargeAt?: string | null;
  canceledAt?: string | null;
  /** The day an auto-renewing membership lasts to (YYYY-MM-DD). */
  membershipValidUntil?: string | null;
  /** The account's IANA zone — the dates are printed on it (null = UTC). */
  timezone?: string | null;
}

/** What Stripe.js needs to show the card form for a NEW card (a SetupIntent — nothing is charged). */
export interface PublicRecurringGiftCard {
  clientSecret: string;
  publishableKey: string;
  /** Stripe.js must be initialised with it (the host's connected account) — null in test mode. */
  connectedAccountId: string | null;
  isTest?: boolean | null;
}

const GIFT_FIELDS = `
  lineKind
  causeName
  membershipName
  organizerName
  organizerSlug
  currencyCode
  amount
  chargeAmount
  billingInterval
  recurringStatus
  isTest
  wantsReceipt
  startedAt
  nextChargeAt
  canceledAt
  membershipValidUntil
  timezone
`;

const GET_QUERY = gql`
  query PublicRecurringGift($params: PublicRecurringGiftTokenInput) {
    publicRecurringGift(params: $params) {
      code
      errors { name errors }
      data { ${GIFT_FIELDS} }
    }
  }
`;

const CANCEL_MUTATION = gql`
  mutation PublicRecurringGiftCancel($params: PublicRecurringGiftTokenInput) {
    publicRecurringGiftCancel(params: $params) {
      code
      errors { name errors }
      data { ${GIFT_FIELDS} }
    }
  }
`;

const CARD_START_MUTATION = gql`
  mutation PublicRecurringGiftCardStart($params: PublicRecurringGiftTokenInput) {
    publicRecurringGiftCardStart(params: $params) {
      code
      errors { name errors }
      data {
        clientSecret
        publishableKey
        connectedAccountId
        isTest
      }
    }
  }
`;

const CARD_FINISH_MUTATION = gql`
  mutation PublicRecurringGiftCardFinish($params: PublicRecurringGiftCardFinishInput) {
    publicRecurringGiftCardFinish(params: $params) {
      code
      errors { name errors }
      data { ${GIFT_FIELDS} }
    }
  }
`;

export class PublicRecurringGiftService {
  /** The gift the emailed link names (BROWSER, through the same-origin proxy). A read: nothing changes. */
  static async get(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { params: { token } }, options);
  }

  /** Stop the monthly gift now — no further charges. Answers the gift as it is after. */
  static async cancel(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(CANCEL_MUTATION, { params: { token } }, options);
  }

  /** A SetupIntent for a new card — confirmed in the browser (`stripe.confirmSetup`), then `cardFinish`. */
  static async cardStart(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(CARD_START_MUTATION, { params: { token } }, options);
  }

  /** The confirmed SetupIntent becomes the gift's card. The server asks Stripe — the id alone proves nothing. */
  static async cardFinish(input: { token: string; setupIntentId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(CARD_FINISH_MUTATION, { params: input }, options);
  }
}
